/**
 * Debounced auto-save hook for the Work Order edit sheet.
 *
 * Extracted from WorkOrderTab.jsx in iter170 (component split step #2)
 * to consolidate the autosave timer + first-load gate + save state
 * machine into one tested unit. Before, this lived as inline refs and
 * a useEffect spread across the parent — every parent re-render
 * armed/reset the timer and the "first load" gate was bypassed by
 * shared closure state with `openEdit` / `openCopy` flows.
 *
 * Contract:
 *   - Fires `updateWorkOrder(editingWO.id, computedPayload)` 1.5s
 *     after the LAST change to `form`, only if a WO is currently open
 *     and we're past the first-load gate.
 *   - Exposes `woSaveStatus` for the UI to render ("Saving..." /
 *     "Saved" / "Save failed"). The `Saved` status auto-clears after
 *     2s so the chip doesn't stick to the header forever.
 *   - Exposes `resetFirstLoad()` so callers (openEdit, openCopy,
 *     onDelete) can suppress the next autosave fire after they
 *     forcibly mutated `form`.
 *
 * Note on deps: the effect intentionally only depends on `form` —
 * including `editingWO`/`clientId` would re-arm the timer on parent
 * re-renders and lose debounced edits. The closure reads the latest
 * values at fire time, which is the correct semantic.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { updateWorkOrder } from "@/lib/api";

export function useWOAutoSave({ form, editingWO, clientId }) {
  const autoSaveTimerRef = useRef(null);
  const isWOFirstLoad = useRef(true);
  const isSavingRef = useRef(false);
  const [woSaveStatus, setWoSaveStatus] = useState("");

  // Reset the first-load gate when the open WO changes.
  useEffect(() => {
    isWOFirstLoad.current = true;
  }, [editingWO?.id]);

  // Imperative reset — callers (openEdit/openCopy/onDelete) use this
  // to suppress the next autosave after they reset `form`.
  const resetFirstLoad = useCallback(() => {
    isWOFirstLoad.current = true;
  }, []);

  useEffect(() => {
    if (isWOFirstLoad.current) {
      isWOFirstLoad.current = false;
      return;
    }
    if (!editingWO) return;
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(async () => {
      if (isSavingRef.current) return;
      if (!form.title?.trim()) return;
      isSavingRef.current = true;
      try {
        setWoSaveStatus("Saving...");
        const li = (form.line_items || []).map((l) => ({
          ...l,
          total: l.quantity * l.unit_price,
        }));
        const cos = (form.change_orders || []).map((co) => ({
          ...co,
          line_items: (co.line_items || []).map((l) => ({
            ...l,
            total: (l.quantity || 0) * (l.unit_price || 0),
          })),
        }));
        const sub = li.reduce((s, l) => s + l.total, 0);
        const coSub = cos
          .filter((co) => co.status === "approved")
          .reduce((s, co) => s + (co.subtotal || 0), 0);
        const revSub = sub + coSub;
        const tax = revSub * ((form.tax_rate || 0) / 100);
        await updateWorkOrder(editingWO.id, {
          ...form,
          client_id: clientId,
          line_items: li,
          change_orders: cos,
          subtotal: revSub,
          tax_amount: tax,
          total: revSub + tax,
        });
        setWoSaveStatus("Saved");
        setTimeout(() => setWoSaveStatus(""), 2000);
      } catch {
        setWoSaveStatus("Save failed");
      } finally {
        isSavingRef.current = false;
      }
    }, 1500);
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
    // Intentional partial deps: see contract note in module header
    // (depending on `editingWO`/`clientId` would re-arm the timer on
    // every parent re-render and lose debounced edits).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  return { woSaveStatus, resetFirstLoad };
}
